import { useState } from 'react';
import { useCart } from '@/context/CartContext';
import type { Product } from '@/mocks/products';

interface ProductInfoProps {
  product: Product;
}

function StarRating({ rating, reviewCount }: { rating: number; reviewCount: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <i
            key={s}
            className={`text-sm ${s <= Math.floor(rating) ? 'ri-star-fill text-[#C4FF61]' : s - 0.5 <= rating ? 'ri-star-half-fill text-[#C4FF61]' : 'ri-star-line text-gray-300'}`}
          ></i>
        ))}
      </div>
      <span className="font-inter text-sm text-gray-500">{rating} ({reviewCount} reviews)</span>
    </div>
  );
}

function CheckoutModal({
  product,
  quantity,
  onClose,
}: {
  product: Product;
  quantity: number;
  onClose: () => void;
}) {
  const [step, setStep] = useState<'summary' | 'form' | 'success'>('summary');
  const [form, setForm] = useState({ name: '', email: '', address: '', card: '' });
  const subtotal = product.price * quantity;
  const shipping = subtotal >= 75 ? 0 : 9.99;
  const total = subtotal + shipping;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('success');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#1A1A1A] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-secure-payment-line text-[#C4FF61] text-lg"></i>
            <span className="font-outfit font-bold text-white text-sm">
              {step === 'success' ? 'Order Confirmed!' : 'Secure Checkout'}
            </span>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer">
            <i className="ri-close-line text-white/70 text-base"></i>
          </button>
        </div>

        {step === 'success' ? (
          <div className="px-6 py-10 flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C4FF61]/20">
              <i className="ri-check-line text-3xl text-[#5a7a00]"></i>
            </div>
            <h3 className="font-outfit font-black text-[#1A1A1A] text-xl">Thank you, {form.name || 'Explorer'}!</h3>
            <p className="font-inter text-gray-500 text-sm leading-relaxed">
              Your order for <strong>{product.name}</strong> has been placed. A confirmation will be sent to <strong>{form.email || 'your email'}</strong>.
            </p>
            <div className="bg-[#F7F8F9] rounded-xl px-5 py-3 w-full text-left">
              <div className="flex justify-between font-outfit text-sm">
                <span className="text-gray-500">Order Total</span>
                <span className="font-bold text-[#1A1A1A]">${total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between font-outfit text-xs mt-1">
                <span className="text-gray-400">Est. Delivery</span>
                <span className="text-gray-600">3–5 Business Days</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-full bg-[#C4FF61] text-[#1A1A1A] font-outfit font-bold py-3 rounded-full hover:bg-[#a8e84a] transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Continue Shopping
            </button>
          </div>
        ) : step === 'summary' ? (
          <div className="px-6 py-5 flex flex-col gap-4">
            {/* Product Row */}
            <div className="flex items-center gap-4 bg-[#F7F8F9] rounded-xl p-3">
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-16 h-16 rounded-lg object-cover object-top shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-outfit font-bold text-[#1A1A1A] text-sm truncate">{product.name}</p>
                <p className="font-inter text-gray-400 text-xs">{product.category} · Qty: {quantity}</p>
              </div>
              <span className="font-outfit font-black text-[#1A1A1A] text-sm shrink-0">${(product.price * quantity).toFixed(2)}</span>
            </div>

            {/* Price Breakdown */}
            <div className="flex flex-col gap-2 text-sm font-inter">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-gray-500">
                <span>Shipping</span>
                <span className={shipping === 0 ? 'text-[#5a7a00] font-semibold' : ''}>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
              </div>
              <div className="h-px bg-gray-100 my-1"></div>
              <div className="flex justify-between font-outfit font-black text-[#1A1A1A] text-base">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>

            <button
              onClick={() => setStep('form')}
              className="w-full bg-[#C4FF61] text-[#1A1A1A] font-outfit font-bold py-3.5 rounded-full hover:bg-[#a8e84a] transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-arrow-right-line"></i>
              Proceed to Payment
            </button>
            <div className="flex items-center justify-center gap-4 text-gray-300">
              {['ri-visa-line', 'ri-mastercard-line', 'ri-paypal-line'].map((icon) => (
                <div key={icon} className="w-6 h-6 flex items-center justify-center">
                  <i className={`${icon} text-xl`}></i>
                </div>
              ))}
              <div className="flex items-center gap-1 text-gray-400">
                <i className="ri-lock-line text-xs"></i>
                <span className="font-inter text-[10px]">SSL Secured</span>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-3">
            <p className="font-outfit font-semibold text-[#1A1A1A] text-sm">Shipping & Payment</p>
            {[
              { label: 'Full Name', key: 'name', type: 'text', placeholder: 'John Doe', icon: 'ri-user-line' },
              { label: 'Email Address', key: 'email', type: 'email', placeholder: 'john@example.com', icon: 'ri-mail-line' },
              { label: 'Shipping Address', key: 'address', type: 'text', placeholder: '123 Trail Rd, Phoenix AZ', icon: 'ri-map-pin-line' },
              { label: 'Card Number', key: 'card', type: 'text', placeholder: '•••• •••• •••• ••••', icon: 'ri-bank-card-line' },
            ].map(({ label, key, type, placeholder, icon }) => (
              <div key={key}>
                <label className="font-outfit text-xs text-gray-500 mb-1 block">{label}</label>
                <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2.5 focus-within:border-[#1A1A1A] transition-colors">
                  <i className={`${icon} text-gray-300 text-sm shrink-0`}></i>
                  <input
                    type={type}
                    placeholder={placeholder}
                    value={form[key as keyof typeof form]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    required
                    className="flex-1 text-sm font-inter text-[#1A1A1A] placeholder-gray-300 outline-none bg-transparent"
                  />
                </div>
              </div>
            ))}
            <div className="flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => setStep('summary')}
                className="flex-1 border border-gray-200 text-gray-500 font-outfit font-semibold py-3 rounded-full hover:border-gray-400 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-[#1A1A1A] text-[#C4FF61] font-outfit font-bold py-3 rounded-full hover:bg-black transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2"
              >
                <i className="ri-lock-line text-xs"></i>
                Place Order
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { addItem } = useCart();

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.images[0],
        category: product.category,
      });
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <>
      <div className="flex flex-col gap-5">
        {/* Category */}
        <p className="font-outfit text-xs uppercase tracking-[0.2em] text-gray-400">
          {product.category}
        </p>

        {/* Title */}
        <h1 className="font-outfit font-black text-[#1A1A1A] text-3xl md:text-4xl leading-tight">
          {product.name}
        </h1>

        {/* Rating */}
        <StarRating rating={product.rating} reviewCount={product.reviewCount} />

        {/* Price */}
        <div className="flex items-baseline gap-3">
          <span className="font-outfit font-black text-3xl text-[#1A1A1A]">
            ${product.price.toFixed(2)}
          </span>
          {product.originalPrice && (
            <span className="font-inter text-lg line-through text-gray-300">
              ${product.originalPrice.toFixed(2)}
            </span>
          )}
          {product.originalPrice && (
            <span className="bg-[#C4FF61] text-[#1A1A1A] font-outfit font-bold text-xs px-2 py-1 rounded-full">
              SAVE ${(product.originalPrice - product.price).toFixed(2)}
            </span>
          )}
        </div>

        {/* Spec Tags */}
        <div className="flex flex-wrap gap-2">
          {product.specs.map((spec) => (
            <span
              key={spec}
              className="border border-gray-200 text-[#1A1A1A] font-outfit font-medium text-xs px-3 py-1.5 rounded-full whitespace-nowrap"
            >
              {spec}
            </span>
          ))}
        </div>

        {/* Description */}
        <p className="font-inter text-gray-600 text-sm leading-relaxed">
          {product.description}
        </p>

        {/* Tech Specs Card */}
        <div className="bg-[#1A1A1A] rounded-2xl p-5">
          <span className="inline-block bg-[#C4FF61] text-[#1A1A1A] font-outfit font-bold text-xs px-3 py-1 rounded-full mb-3">
            KEY FEATURES
          </span>
          <ul className="space-y-2">
            {product.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-check-line text-[#C4FF61] text-sm"></i>
                </div>
                <span className="font-inter text-white/70 text-sm leading-snug">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Quantity Selector */}
        <div className="flex items-center border border-gray-200 rounded-full overflow-hidden bg-white w-fit">
          <button
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            className="w-10 h-12 flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors text-[#1A1A1A]"
            aria-label="Decrease quantity"
          >
            <i className="ri-subtract-line text-sm"></i>
          </button>
          <span className="w-10 text-center font-outfit font-bold text-[#1A1A1A]">{quantity}</span>
          <button
            onClick={() => setQuantity((q) => q + 1)}
            className="w-10 h-12 flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors text-[#1A1A1A]"
            aria-label="Increase quantity"
          >
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col gap-3">
          {/* Buy Now */}
          <button
            onClick={() => setCheckoutOpen(true)}
            disabled={!product.inStock}
            className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-outfit font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
              product.inStock
                ? 'bg-[#1A1A1A] text-[#C4FF61] hover:bg-black'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <i className="ri-secure-payment-line"></i>
            Buy Now
          </button>

          {/* Add to Cart */}
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock}
            className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-outfit font-bold text-sm transition-all cursor-pointer whitespace-nowrap border-2 ${
              added
                ? 'border-[#C4FF61] bg-[#C4FF61]/10 text-[#5a7a00]'
                : product.inStock
                ? 'border-gray-200 text-[#1A1A1A] hover:border-[#1A1A1A] bg-white'
                : 'border-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <i className={added ? 'ri-check-line' : 'ri-shopping-bag-line'}></i>
            {added ? 'Added to Cart!' : product.inStock ? 'Add to Cart' : 'Out of Stock'}
          </button>
        </div>

        {/* Wishlist */}
        <button className="flex items-center justify-center gap-2 border border-gray-200 py-3 rounded-full font-outfit font-medium text-sm text-gray-500 hover:border-[#1A1A1A] hover:text-[#1A1A1A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-heart-line"></i>
          Add to Wishlist
        </button>

        {/* Guarantees */}
        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-gray-100">
          {[
            { icon: 'ri-truck-line', label: 'Free Shipping', sub: 'Orders $75+' },
            { icon: 'ri-refresh-line', label: '60-Day Returns', sub: 'No hassle' },
            { icon: 'ri-shield-check-line', label: '2-Year Warranty', sub: 'All products' },
          ].map((g) => (
            <div key={g.label} className="flex flex-col items-center text-center gap-1">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${g.icon} text-[#C4FF61] text-lg`}></i>
              </div>
              <p className="font-outfit font-semibold text-[#1A1A1A] text-xs">{g.label}</p>
              <p className="font-inter text-gray-400 text-[10px]">{g.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Checkout Modal */}
      {checkoutOpen && (
        <CheckoutModal
          product={product}
          quantity={quantity}
          onClose={() => setCheckoutOpen(false)}
        />
      )}
    </>
  );
}
